'use client'
import React, { useEffect, useState } from "react";
import { Comment, formatDateString } from "@/app/utils/interfaces";

interface CommentSectionProps {
  newsId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ newsId }) => {
    console.log(newsId);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newCommentName, setNewCommentName] = useState("");
  const [newCommentText, setNewCommentText] = useState("");
  const [replyName, setReplyName] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);

  const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}news/${newsId}/comments/`);
        if (!response.ok) throw new Error('Failed to fetch article data');
        const data = await response.json();
        setComments(data);
        setLoading(false);
      } catch (err: any) {
        setError(err.message);
        setLoading(false);
      }
    };
    if (newsId) fetchData();
  }, [newsId, API_BASE_URL]);


  const handleAddComment = async () => {
    if (!newCommentName.trim() || !newCommentText.trim()) return;
    try {
      const response=await fetch(`${API_BASE_URL}news/comment/post/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          news_id: newsId,
          name: newCommentName,
          content: newCommentText,
        }),
      });

      if (!response.ok) {
        console.error("Failed to post comment");
        return;
      }
  
      const createdComment = await response.json();
  
      const newComment: Comment = {
        id: createdComment.id,
        ip: createdComment.ip,
        name: createdComment.name,
        content: createdComment.content,
        created_at: createdComment.created_at,
        replies: createdComment.replies
      };

      setComments([newComment, ...comments]);
      setNewCommentName("");
      setNewCommentText("");
    } catch (error) {
      console.error("Failed to post comment", error);
    }
  };

  const handleAddReply = async () => {
    if (!replyName.trim() || !replyText.trim() || replyingTo === null) return;

    const addReplyToComment = (
      comments: Comment[],
      commentId: number,
      reply: Comment
    ): Comment[] => {
      return comments.map((comment) => {
        if (comment.id === commentId) {
          return { ...comment, replies: [...comment.replies, reply] };
        } else if (comment.replies.length > 0) {
          return {
            ...comment,
            replies: addReplyToComment(comment.replies, commentId, reply),
          };
        }
        return comment;
      });
    };

    try {
      const response = await fetch(`${API_BASE_URL}news/comment/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parent_comment_id: replyingTo,
          news_id: newsId,
          name: replyName,
          content: replyText,
        }),
      });

      if (!response.ok) {
        console.error("Failed to post comment");
        return;
      }
  
      const createdComment = await response.json();
  
      const newReply: Comment = {
        id: createdComment.id,
        ip: createdComment.ip,
        name: createdComment.name,
        content: createdComment.content,
        created_at: createdComment.created_at,
        replies: createdComment.replies
      };

      setComments((prevComments) =>
        addReplyToComment(prevComments, replyingTo, newReply)
      );
      setReplyingTo(null);
      setReplyName("");
      setReplyText("");
    } catch (error) {
      console.error("Failed to post reply", error);
    }
  };

  const renderReplyBox = () => (
    <div className="mt-2">
      <input
        type="text"
        placeholder="Нэр"
        value={replyName}
        onChange={(e) => setReplyName(e.target.value)}
        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300 mb-2"
      />
      <textarea
        placeholder="Хариу"
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        rows={2}
        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300 mb-2"
      />
      <div className="flex gap-2">
        <button
          onClick={handleAddReply}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg  transition"
        >
          Хариу илгээх
        </button>
        <button
          onClick={() => setReplyingTo(null)}
          className="text-gray-600 text-sm hover:underline"
        >
          Болих
        </button>
      </div>
    </div>
  );

  const renderComments = (comments: Comment[]) => {
    return comments.map((comment, index) => (
      <div key={index} className="ml-4 border-l pl-4 mb-4">
        <div className="p-4 border rounded-lg bg-gray-50 shadow-sm mt-2">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span className="font-medium">{comment.name}</span>
            <span>{formatDateString(comment.created_at)}</span>
          </div>
          <p className="text-gray-800 mt-2">{comment.content}</p>
          {/* <span className="text-xs text-gray-500">IP: {comment.ip}</span> */}
          <button
            onClick={() => setReplyingTo(comment.id)}
            className="text-blue-600 text-sm mt-2 ms-2 hover:underline"
          >
            Хариулах
          </button>
        </div>
        {replyingTo === comment.id && renderReplyBox()}
        {renderComments(comment.replies)}
      </div>
    ));
  };

  return (
    <div className="">
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">Сэтгэгдэл бичих</h2>
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Нэр"
            value={newCommentName}
            onChange={(e) => setNewCommentName(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
          />
          <textarea
            placeholder="Сэтгэгдэл"
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
          />
          <button
            onClick={handleAddComment}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg transition"
          >
            Илгээх
          </button>
        </div>
      </div>

      <div>
        <div className="mt-4">{renderComments(comments)}</div>
      </div>
    </div>
  );
};

export default CommentSection;
